import React, { useState } from 'react';
import { DriverFormData } from '../../types/driver';

interface UploadedFileUrls {
  vehiclePhotos: string[];
}

interface VehicleStepProps {
  data: DriverFormData;
  updateData: (data: Partial<DriverFormData>) => void;
  onFileUpload: (files: File[], type: keyof UploadedFileUrls) => Promise<void>;
  uploadedUrls: {
    vehiclePhotos: string[];
  };
  onRemoveVehiclePhoto: (index: number) => void;
}

const VehicleStep: React.FC<VehicleStepProps> = ({ 
  data, 
  updateData, 
  onFileUpload,
  uploadedUrls,
  onRemoveVehiclePhoto
}) => {
  const [uploading, setUploading] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    updateData({ [name]: value });
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const { files } = e.target;
    if (files && files.length > 0) {
      setUploading(true);
      try {
        await onFileUpload(Array.from(files), 'vehiclePhotos');
      } catch (error) {
        alert('Failed to upload vehicle photos');
      } finally {
        setUploading(false);
      }
    }
  };

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 30 }, (_, i) => currentYear - i);

  return (
    <div>
      <h2 className="text-2xl font-bold text-gray-900 mb-6">Vehicle Information</h2>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Plate Number (Vehicle Registration Number) *
          </label>
          <input
            type="text"
            name="vehicleRegistration"
            value={data.vehicleRegistration}
            onChange={handleChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Vehicle Make *
          </label>
          <input
            type="text"
            name="vehicleMake"
            value={data.vehicleMake}
            onChange={handleChange}
            placeholder="e.g., Toyota, Honda, Ford"
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Vehicle Model *
          </label>
          <input
            type="text"
            name="vehicleModel"
            value={data.vehicleModel}
            onChange={handleChange}
            placeholder="e.g., Camry, Civic, Focus"
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Vehicle Color *
          </label>
          <input
            type="text"
            name="vehicleColor"
            value={data.vehicleColor}
            onChange={handleChange}
            placeholder="e.g., Red, Blue, Black"
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Vehicle Year *
          </label>
          <select
            name="vehicleYear"
            value={data.vehicleYear}
            onChange={handleChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            required
          >
            <option value="">Select year</option>
            {years.map(year => (
              <option key={year} value={year.toString()}>
                {year}
              </option>
            ))}
          </select>
        </div>

        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Vehicle Photos *
          </label>
          <input
            type="file"
            multiple
            onChange={handleFileChange}
            accept="image/*"
            disabled={uploading}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50"
            required
          />
          
          <div className="flex items-center mt-2">
            {uploading && (
              <div className="text-blue-600 text-sm">Uploading photos...</div>
            )}
            {uploadedUrls.vehiclePhotos.length > 0 && !uploading && (
              <div className="text-green-600 text-sm">
                ✓ {uploadedUrls.vehiclePhotos.length} photo(s) uploaded successfully
              </div>
            )}
          </div>

          <p className="text-xs text-gray-500 mt-1">
            Upload clear photos of your vehicle (front, back, sides, interior). You can select multiple files.
          </p>

          {uploadedUrls.vehiclePhotos.length > 0 && (
            <div className="mt-4">
              <h4 className="text-sm font-medium text-gray-700 mb-2">Uploaded Photos:</h4>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {uploadedUrls.vehiclePhotos.map((url, index) => (
                  <div key={index} className="relative group">
                    <img
                      src={`${process.env.REACT_APP_API_URL}${url}`}
                      alt={`Vehicle ${index + 1}`}
                      className="w-full h-24 object-cover rounded-md border border-gray-300"
                    />
                    <button
                      type="button"
                      onClick={() => onRemoveVehiclePhoto(index)}
                      className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default VehicleStep;